import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import styled from 'styled-components';
import axios from 'axios';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  PlusCircle, 
  Activity, 
  Search, 
  RefreshCcw, 
  Filter, 
  ChevronRight, 
  ChevronDown, 
  X, 
  Download, 
  MessageSquare,
  Zap,
  TrendingUp,
  Package,
  Layers,
  Calendar,
  Shield
} from 'lucide-react';
import AIChatPanel from './AIChatPanel';
import { isGovernmentBond } from '../utils/fixMessageGenerator';
import ContextMenu from './shared/ContextMenu';
import { default as BlockingOverlay } from './shared/LoadingOverlay';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { cn } from '../lib/utils';
import { getTradeEntryPathForTicketType } from '../utils/tradeEntryRouting';

import { DateField } from './shared/DateField';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = 
  ({ label, value, icon, color }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Interfaces ==============
interface BondTransaction {
  id: number;
  transactionType: string;
  assetManager?: string;
  assetManagerName?: string;
  portfolioManager?: string;
  portfolioManagerName?: string;
  assetClass?: string;
  assetClassName?: string;
  assetSubClass?: string;
  assetSubClassName?: string;
  security?: string;
  securityName?: string;
  cusip?: string;
  price?: number;
  portfolio?: string;
  portfolioName?: string;
  amount?: number;
  tradeDate?: string;
  settleDate?: string;
  status?: string;
  securityType?: string;
  ticketType?: string;
  eventType?: string;
  sourceRef?: string;
  legGroupRef?: string;
  isPackage?: boolean;
  lastActivity?: string;
  notes?: string;
  allocations?: any[];
}

/**
 * Maps a blotter row to the trade-entry sub-route. Must match routes in
 * InstitutionalTradeEntry (e.g. derivatives/cd-swap). A bare "derivatives"
 * path is invalid and falls through to fixed-income, breaking deep-links.
 */
function getTradeEntryPathForRow(row: {
  assetClass?: string;
  assetClassName?: string;
  securityType?: string;
  security?: string;
  securityName?: string;
  ticketType?: string;
  transactionType?: string;
  eventType?: string;
}): string {
  // Unified-IBOR: ticketType is the authoritative signal for new writes. Fall
  // back to asset-class/security-type heuristics only for legacy rows where
  // ticketType is NULL. See memory/project_ticket_type_field.md.
  const ticketType = (row.ticketType || '').toUpperCase();
  const transactionType = (row.transactionType || '').toUpperCase();
  const eventType = (row.eventType || '').toUpperCase();
  const isCreditTicket = ticketType === 'PRIVATE_CREDIT' || ticketType === 'LEVERAGED_LOAN';
  const isPostedLifecycleEvent =
    transactionType === 'COUPON' ||
    transactionType === 'REVALUATION' ||
    eventType === 'ACCRUAL' ||
    eventType === 'MTM' ||
    eventType === 'RATE_RESET' ||
    eventType === 'AMENDMENT' ||
    eventType === 'PIK';
  if (isCreditTicket && isPostedLifecycleEvent) {
    return 'posted-credit-event';
  }

  const authoritativePath = getTradeEntryPathForTicketType(ticketType);
  if (authoritativePath) {
    return authoritativePath;
  }

  const assetClass = (row.assetClass || row.assetClassName || '').toLowerCase();
  const securityType = (row.securityType || '').toLowerCase();
  const securityLabel = (row.security || row.securityName || '').toLowerCase();
  const combined = `${assetClass} ${securityType} ${securityLabel}`;

  if (combined.includes('equity') || combined.includes('stock') || combined.includes('common')) {
    return 'equity';
  }
  if (
    securityType.includes('credit default') ||
    combined.includes('credit default swap')
  ) {
    return 'derivatives/cd-swap';
  }
  if (combined.includes('future')) {
    return 'derivatives/futures';
  }
  if (combined.includes('option')) {
    return 'derivatives/options';
  }
  if (combined.includes('fx') || combined.includes('foreign exchange') || combined.includes('currency')) {
    return 'fx/spot';
  }
  if (combined.includes('repo')) {
    return 'repo';
  }
  // Syndicated/leveraged loans — legacy rows where ticketType wasn't stamped.
  if (
    securityType.includes('term_loan') ||
    securityType.includes('term loan') ||
    securityType.includes('revolver') ||
    securityType.includes('ddtl') ||
    securityType.includes('syndicated') ||
    securityType.includes('leveraged') ||
    combined.includes('term loan') ||
    combined.includes('syndicated loan')
  ) {
    return 'leveraged-loan';
  }
  if (combined.includes('private') && (combined.includes('credit') || combined.includes('fixed'))) {
    return 'private-fixed-income';
  }
  if (combined.includes('real estate') || combined.includes('realestate') || combined.includes('cml')) {
    return 'real-estate';
  }
  if (combined.includes('derivative') || combined.includes('swap')) {
    return 'derivatives/ir-swap';
  }
  return 'fixed-income';
}

interface AllocationRow extends BondTransaction {
  isAllocation: boolean;
  blockId: number;
  blockAmount: number;
  allocationPortfolio: string;
  allocationAmount: number;
  allocationPercentage?: number;
}

interface FilterState {
  transactionType?: string;
  status?: string;
  portfolio?: string;
  assetClass?: string;
  assetManager?: string;
  portfolioManager?: string;
  search?: string;
  tradeDateFrom?: string;
  tradeDateTo?: string;
}

/** Which slice of the book the Blotter lists when no search is typed. */
type BlotterScope = 'working' | 'today' | 'all';

interface BlotterSummary {
  total: number;
  volume: number;
  buys: number;
  sells: number;
}

interface PortfolioOption {
  id: number;
  name: string;
}

const SCOPES: BlotterScope[] = ['working', 'today', 'all'];

const SCOPE_LABELS: Record<BlotterScope, { label: string; hint: string }> = {
  working: { label: 'Working', hint: 'Every ticket not yet posted, from any date, plus everything traded on the business date' },
  today: { label: 'Today', hint: 'Everything traded on the business date' },
  all: { label: 'All', hint: 'Every trade in the book' },
};

/** Older links use two status spellings the server does not know. */
const STATUS_ALIASES: Record<string, string> = {
  APPROVED: 'PRECLEAR_APPROVED',
  PENDING_PRECLEAR: 'PRECLEAR_PENDING',
};
const normalizeStatus = (status?: string): string | undefined => {
  if (!status) return undefined;
  const upper = status.trim().toUpperCase();
  return STATUS_ALIASES[upper] || upper;
};

/** Grid columns the server can order by, mapped to the API's sort keys. Other columns are not sortable. */
const SERVER_SORT_FIELDS: Record<string, string> = {
  id: 'id',
  tradeDate: 'tradeDate',
  settleDate: 'settleDate',
  amount: 'amount',
  quantity: 'quantity',
  status: 'status',
  transactionType: 'transactionType',
  cusip: 'cusip',
  security: 'security',
  portfolio: 'portfolio',
};

const PAGE_SIZES = [50, 100, 200];

/** A bare Blotter opens on Working; a link that names a filter opens on All so the filter means what it says. */
const scopeFromParams = (params: URLSearchParams): BlotterScope => {
  const view = (params.get('view') || '').trim().toLowerCase();
  if (view === 'working' || view === 'today' || view === 'all') return view;
  if ((params.get('date') || '').trim().toLowerCase() === 'today') return 'today';
  const filtered = ['status', 'type', 'portfolio', 'query', 'assetClass'].some(key => params.get(key))
    || params.get('action') === 'preclear';
  return filtered ? 'all' : 'working';
};

const ABF_NON_CASH_EVENTS = new Set([
  'REVALUE',
  'REVALUATION',
  'MTM',
  'MARK_TO_MARKET',
  'BORROWING_BASE',
  'BBC',
  'AMENDMENT',
  'CONTROL',
  'COVENANT',
  'COVENANT_TEST',
  'ACCRUAL'
]);

const isAbfRow = (row: Partial<BondTransaction> | any) => {
  const ticketType = String(row?.ticketType || '').toUpperCase();
  const assetClass = String(row?.assetClass || row?.assetClassName || '').toUpperCase();
  const securityType = String(row?.securityType || '').toUpperCase();
  return ticketType === 'ABF'
    || assetClass === 'ASSET-BASED FINANCE'
    || assetClass === 'ASSET_BASED_FINANCE'
    || securityType.includes('ABF FACILITY');
};

const isAbfNonCashEvent = (row: Partial<BondTransaction> | any) => {
  if (!isAbfRow(row)) return false;
  const transactionType = String(row?.transactionType || '').toUpperCase();
  const eventType = String(row?.eventType || '').toUpperCase();
  const sourceRef = String(row?.sourceRef || '').toUpperCase();
  const amount = Number(row?.amount ?? row?.allocationAmount ?? 0);
  return ABF_NON_CASH_EVENTS.has(transactionType)
    || ABF_NON_CASH_EVENTS.has(eventType)
    || sourceRef.includes('BORROWING-BASE')
    || sourceRef.includes('BORROWING_BASE')
    || sourceRef.includes('REVALUE')
    || sourceRef.includes('MTM')
    || (amount === 1 && transactionType !== 'BUY' && transactionType !== 'SELL');
};

const formatTransactionType = (row: Partial<BondTransaction> | any) => {
  const ticketType = String(row?.ticketType || '').toUpperCase();
  const eventType = String(row?.eventType || '').toUpperCase();
  const isLoanLifecycle = ['PRIVATE_CREDIT', 'LEVERAGED_LOAN', 'DIRECT_LENDING'].includes(ticketType)
    && ['DRAWDOWN', 'REPAY', 'PAYOFF', 'RATE_RESET', 'AMENDMENT', 'PIK', 'MTM'].includes(eventType);
  const isAbfLifecycle = ticketType === 'ABF'
    && ['DRAWDOWN', 'REPAY', 'PAYOFF', 'DISPOSE', 'RATE_RESET', 'AMENDMENT', 'MTM'].includes(eventType);
  const value = String(isLoanLifecycle || isAbfLifecycle ? eventType : (row?.transactionType || row?.eventType || '')).toUpperCase();
  if (isAbfNonCashEvent(row)) {
    if (value.includes('BORROW')) return 'BBC';
    if (value.includes('REVALU') || value.includes('MTM') || value.includes('MARK')) return 'REVALUE';
    if (value.includes('ACCRUAL')) return 'ACCRUAL';
    return value || 'CONTROL';
  }
  return value || '--';
};

// Helper to get initial filters from URL params
const getInitialFiltersFromURL = (): FilterState => {
  const params = new URLSearchParams(window.location.search);
  const urlStatus = params.get('status');
  const urlType = params.get('type');
  const urlPortfolio = params.get('portfolio');
  const urlAction = params.get('action');
  const urlQuery = params.get('query');
  const urlAssetClass = params.get('assetClass');

  if (urlStatus || urlType || urlPortfolio || urlAction || urlQuery || urlAssetClass) {
    const urlFilters: FilterState = {};
    if (urlStatus) urlFilters.status = normalizeStatus(urlStatus);
    if (urlType) urlFilters.transactionType = urlType;
    if (urlPortfolio) urlFilters.portfolio = urlPortfolio;
    if (urlAction === 'preclear') urlFilters.status = 'DRAFT';
    if (urlQuery) urlFilters.search = urlQuery;
    // LL preset (ll-construction-surface-redesign.md D4): ?assetClass=LL is the
    // deep-link the retired Secondary blotter tab redirects to.
    if (urlAssetClass) urlFilters.assetClass = urlAssetClass === 'LL' ? 'Leveraged Loan' : urlAssetClass;
    console.log('[Blotter] Initialized filters from URL:', urlFilters);
    return urlFilters;
  }
  return {};
};

const BondTransactionScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences } = useUserPreferences();
  const gridRef = useRef<AgGridReact>(null);
  const urlParamsProcessedRef = useRef(false);

  const [rowData, setRowData] = useState<BondTransaction[] | AllocationRow[]>([]);
  const [allRowData, setAllRowData] = useState<BondTransaction[] | AllocationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'block' | 'allocation' | 'packages'>('block');
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(() => {
    // Open filter sidebar if URL has filter params
    const params = new URLSearchParams(window.location.search);
    return !!(params.get('status') || params.get('type') || params.get('portfolio') || params.get('action'));
  });
  const [filters, setFilters] = useState<FilterState>(getInitialFiltersFromURL);
  const [scope, setScope] = useState<BlotterScope>(() => scopeFromParams(new URLSearchParams(window.location.search)));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [serverSort, setServerSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'id', dir: 'desc' });
  const [totalRows, setTotalRows] = useState(0);
  const [summary, setSummary] = useState<BlotterSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [portfolioOptions, setPortfolioOptions] = useState<PortfolioOption[]>([]);
  const [portfolioOptionsLoaded, setPortfolioOptionsLoaded] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState((getInitialFiltersFromURL().search || '').trim());
  const requestSeq = useRef(0);
  const lastQueryKey = useRef('');
  // D4 (ll-construction-surface-redesign.md): the LL view can overlay the 10
  // pre-companion loan_trade seed rows (read-only history) from the union
  // endpoint the retired Secondary blotter used.
  const [includeLegacyLL, setIncludeLegacyLL] = useState(false);
  const [legacyLLRows, setLegacyLLRows] = useState<any[]>([]);
  const [blockingOperation, setBlockingOperation] = useState({ isActive: false, message: '', subMessage: '' });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; trade: BondTransaction | null } | null>(null);

  // Extended status list to include full trade lifecycle
  const statuses = ['DRAFT', 'PRECLEAR_PENDING', 'PRECLEAR_APPROVED', 'PRECLEAR_REJECTED', 'READY', 'SENT', 'EXECUTED', 'POSTED', 'SETTLED', 'CANCELLED', 'VOIDED', 'REVERSED'];
  const types = ['BUY', 'SELL'];

  // The dropdown lists the portfolio master, not whichever rows happen to be loaded.
  useEffect(() => {
    axios.get('/api/dimensions/portfolios', { params: { size: 1000 } })
      .then(res => {
        const raw: any[] = Array.isArray(res.data) ? res.data : (res.data?.content || []);
        setPortfolioOptions(raw
          .map(p => ({ id: Number(p.id ?? p.portfolioId), name: String(p.portfolioName || p.name || p.portfolioCode || p.id) }))
          .filter(p => Number.isFinite(p.id))
          .sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => setPortfolioOptions([]))
      .finally(() => setPortfolioOptionsLoaded(true));
  }, []);

  const portfolios = useMemo(() => portfolioOptions.map(p => p.name), [portfolioOptions]);

  // A portfolio arrives as a name (dropdown, older links) or an id; the server filters by id.
  const portfolioId = useMemo(() => {
    if (!filters.portfolio) return undefined;
    const wanted = filters.portfolio.trim().toLowerCase();
    return portfolioOptions.find(p => p.name.toLowerCase() === wanted || String(p.id) === wanted)?.id;
  }, [filters.portfolio, portfolioOptions]);
  const portfolioUnresolved = Boolean(filters.portfolio) && portfolioOptionsLoaded && portfolioId === undefined;

  // Search goes to the server a moment after typing stops.
  useEffect(() => {
    const next = (filters.search || '').trim();
    const timer = window.setTimeout(() => setDebouncedSearch(next), 300);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const searchActive = debouncedSearch.length > 0;

  // Everything the server filters on. A search always covers every trade, whichever scope is selected.
  const serverFilterParams = useMemo(() => {
    const params: Record<string, string | number> = { view: searchActive ? 'all' : scope };
    const status = normalizeStatus(filters.status);
    if (status) params.status = status;
    if (filters.transactionType) params.type = filters.transactionType;
    if (portfolioId !== undefined) params.portfolioId = portfolioId;
    if (filters.assetClass) params.assetClass = filters.assetClass;
    if (searchActive) params.search = debouncedSearch;
    if (filters.tradeDateFrom) params.fromDate = filters.tradeDateFrom;
    if (filters.tradeDateTo) params.toDate = filters.tradeDateTo;
    return params;
  }, [searchActive, scope, filters.status, filters.transactionType, portfolioId, filters.assetClass,
      debouncedSearch, filters.tradeDateFrom, filters.tradeDateTo]);

  // Track if URL params have been applied (for this mount)
  const initialFiltersFromURL = useRef(Object.keys(getInitialFiltersFromURL()).length > 0);

  // Handle URL params - runs whenever searchParams changes (for in-app navigation)
  useEffect(() => {
    // Skip on initial mount - already handled by useState initializer
    if (!urlParamsProcessedRef.current) {
      urlParamsProcessedRef.current = true;
      if (initialFiltersFromURL.current) {
        console.log('[Blotter] Initial URL filters already applied via useState');
        return;
      }
    }

    const urlStatus = searchParams.get('status');
    const urlType = searchParams.get('type');
    const urlPortfolio = searchParams.get('portfolio');
    const urlAction = searchParams.get('action');
    const urlQuery = searchParams.get('query');
    const urlAssetClass = searchParams.get('assetClass');

    const hasUrlParams = urlStatus || urlType || urlPortfolio || urlAction || urlQuery || urlAssetClass;

    // If URL has filter params, apply them (for subsequent navigations)
    if (hasUrlParams) {
      console.log('[Blotter] Applying URL params from navigation:', { urlStatus, urlType, urlPortfolio, urlAction, urlQuery });

      const urlFilters: FilterState = {};
      if (urlStatus) urlFilters.status = normalizeStatus(urlStatus);
      if (urlType) urlFilters.transactionType = urlType;
      if (urlPortfolio) urlFilters.portfolio = urlPortfolio;
      if (urlAction === 'preclear') urlFilters.status = 'DRAFT';
      if (urlQuery) urlFilters.search = urlQuery;
      if (urlAssetClass) urlFilters.assetClass = urlAssetClass === 'LL' ? 'Leveraged Loan' : urlAssetClass;

      setFilters(urlFilters);
      setIsFilterSidebarOpen(true);
    }
    if (hasUrlParams || searchParams.get('view') || searchParams.get('date')) {
      setScope(scopeFromParams(searchParams));
    }
  }, [searchParams]);

  // Apply default date filters only if no filters are set
  useEffect(() => {
    // Skip if URL params provided initial filters
    if (initialFiltersFromURL.current) {
      console.log('[Blotter] Skipping defaults - URL params provided initial filters');
      return;
    }

    // Check if current filters are empty (no URL params or user selections)
    setFilters(currentFilters => {
      if (Object.keys(currentFilters).length > 0) {
        return currentFilters; // Keep existing filters
      }

      // Apply preferences or default date range
      if (preferences?.defaultBlotterFilters && Object.keys(preferences.defaultBlotterFilters).length > 0) {
        console.log('[Blotter] Applying preference filters');
        return preferences.defaultBlotterFilters as FilterState;
      } else {
        // No default date filter — show all trades. Users can apply their own
        // date range via the filter panel. Defaulting to a narrow window hides
        // historical acquisitions booked from documents with past trade dates.
        return {};
      }
    });
  }, [preferences]);

  const fetchBondTransactions = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      if (viewMode === 'packages') {
        const groupsRes = await axios.get('/api/trade-groups', { params: { limit: 500 } });
        const groups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
        setAllRowData(groups.map((g: any) => ({
          id: g.legGroupRef,
          transactionType: g.reversal ? 'REVERSAL' : 'PACKAGE',
          cusip: String(g.legGroupRef).replace(/^GRP-/, ''),
          security: g.portfolioName || '',
          portfolio: g.portfolioName || '',
          amount: g.legCount,
          tradeDate: g.tradeDate || '',
          status: g.status || '',
          legGroupRef: g.legGroupRef,
          lastActivity: g.lastActivity || '',
          isPackage: true,
        })) as any);
        setTotalRows(groups.length);
        setSummary(null);
        setLoadError(null);
        return;
      }

      // A filter, sort or page-size change starts again at the first page.
      const queryKey = `${JSON.stringify(serverFilterParams)}|${pageSize}|${serverSort.field},${serverSort.dir}`;
      if (lastQueryKey.current && queryKey !== lastQueryKey.current && page !== 0) {
        lastQueryKey.current = queryKey;
        setPage(0);
        return;
      }
      lastQueryKey.current = queryKey;

      if (filters.portfolio && !portfolioOptionsLoaded) return; // wait until the portfolio name can become an id
      if (portfolioUnresolved) {
        setAllRowData([]);
        setTotalRows(0);
        setSummary({ total: 0, volume: 0, buys: 0, sells: 0 });
        setLoadError(`Portfolio "${filters.portfolio}" was not found.`);
        return;
      }

      const seq = ++requestSeq.current;
      const [response, summaryResponse] = await Promise.all([
        axios.get('/api/trade-transactions', {
          params: { ...serverFilterParams, page, size: pageSize, sort: `${serverSort.field},${serverSort.dir}` },
        }),
        axios.get('/api/trade-transactions/summary', { params: serverFilterParams }).catch(() => null),
      ]);
      if (seq !== requestSeq.current) return; // a newer request owns the screen
      const transactions = response.data?.content || [];
      setTotalRows(Number(response.data?.totalElements ?? transactions.length));
      setSummary(summaryResponse?.data ?? null);
      setLoadError(null);
      
      const resolveAssetClass = (t: any): string => {
        // ticketType is the authoritative signal for new-style trades
        const tt = (t.ticketType || '').toUpperCase();
        if (tt === 'RE' || tt === 'RE_DEBT') return 'Real Estate';
        if (tt === 'PE_COMMITMENT') return 'Private Equity';
        if (tt === 'ABF') return 'Asset-Based Finance';
        if (tt === 'PRIVATE_CREDIT') return 'Private Credit';
        if (tt === 'LEVERAGED_LOAN') return 'Leveraged Loan';
        if (tt === 'CLO_TRANCHE') return 'CLO';
        if (tt === 'PUBLIC_FI') return 'Fixed Income';
        if (tt === 'IRS' || tt === 'TRS' || tt === 'CDS' || tt === 'CDX' || tt === 'CCS' || tt === 'SWAPTION') return 'Derivatives';
        if (tt === 'FX') return 'FX';
        // Fall back to assetClassName, then securityType — normalize legacy uppercase values
        const raw = t.assetClassName || t.assetClass || t.securityType || '';
        if (raw.toUpperCase() === 'PRIVATE_EQUITY') return 'Private Equity';
        if (raw.toUpperCase() === 'ASSET_BASED_FINANCE' || raw.toUpperCase() === 'ABF') return 'Asset-Based Finance';
        if (raw.toUpperCase() === 'PRIVATE_CREDIT') return 'Private Credit';
        if (raw.toUpperCase() === 'LEVERAGED_LOAN') return 'Leveraged Loan';
        return raw;
      };

      const mapped = transactions.map((t: any) => ({
        ...t,
        assetManager: t.assetManagerName || t.assetManager || '',
        portfolioManager: t.portfolioManagerName || t.portfolioManager || '',
        assetClass: t.blotterAssetClass || resolveAssetClass(t),
        security: t.securityName || t.security || '',
        portfolio: t.portfolioName || t.portfolio || '',
        status: t.status || 'DRAFT'
      }));

      if (viewMode === 'allocation') {
        const allocationRows: AllocationRow[] = [];
        mapped.forEach((t: any) => {
          if (t.allocations?.length) {
            t.allocations.forEach((a: any) => {
              allocationRows.push({
                ...t,
                isAllocation: true,
                blockId: t.id,
                blockAmount: t.amount || 0,
                allocationPortfolio: a.portfolioName,
                allocationAmount: a.allocatedAmount,
                allocationPercentage: a.percentage
              });
            });
          } else {
            allocationRows.push({ ...t, isAllocation: false, blockId: t.id, blockAmount: t.amount || 0, allocationPortfolio: t.portfolio, allocationAmount: t.amount, allocationPercentage: 100 });
          }
        });
        setAllRowData(allocationRows);
      } else {
        setAllRowData(mapped);
      }
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      setAllRowData([]);
      setTotalRows(0);
      setSummary(null);
      setLoadError(error?.response?.data?.error || 'The Blotter could not load trades. Refresh to try again.');
    } finally {
      setIsLoading(false);
    }
  }, [viewMode, serverFilterParams, page, pageSize, serverSort, filters.portfolio, portfolioOptionsLoaded, portfolioUnresolved]);

  useEffect(() => { fetchBondTransactions(true); }, [fetchBondTransactions]);

  const handleSendAllocation = async (venue: 'Tradeweb' | 'MarketAxess' | 'EMSX', trade: BondTransaction) => {
    if (!trade.id) {
      alert('Cannot send: Trade ID is missing');
      return;
    }

    const sessionName = venue.toUpperCase();

    setBlockingOperation({
      isActive: true,
      message: `Sending Allocation to ${venue}...`,
      subMessage: 'Connecting to FIX session'
    });

    try {
      let response;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          response = await axios.post('/api/fix/allocations/send', {
            tradeTransactionId: trade.id,
            sessionName: sessionName
          });
          break;
        } catch (sendError: any) {
          const errorMsg = sendError.response?.data?.error || sendError.message || '';
          if ((errorMsg.includes('not registered') || errorMsg.includes('not logged on')) && retryCount === 0) {
            try {
              await axios.post('/api/fix/sessions/reload');
              await new Promise(resolve => setTimeout(resolve, 5000));
              retryCount++;
              continue;
            } catch (reloadError) {
              throw sendError;
            }
          } else {
            throw sendError;
          }
        }
      }
      
      if (response?.data.success) {
        alert(`✅ Allocation instruction sent to ${venue} successfully!`);
        fetchBondTransactions();
      } else {
        alert(`❌ Failed to send allocation to ${venue}: ${response?.data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      if (errorMsg.includes('not registered') || errorMsg.includes('not logged on')) {
        const reload = window.confirm(`❌ FIX session for ${venue} is not connected. Reload?`);
        if (reload) {
          try {
            await axios.post('/api/fix/sessions/reload');
            alert(`✅ FIX sessions reloaded. Please wait 5 seconds then try again.`);
          } catch (reloadError: any) {
            alert(`❌ Failed to reload sessions.`);
          }
        }
      } else {
        alert(`❌ Error sending allocation to ${venue}:\n${errorMsg}`);
      }
    } finally {
      setBlockingOperation({ isActive: false, message: '', subMessage: '' });
    }
  };

  // D17: generic reversal of a POSTED trade — offsetting economics as-of today,
  // linked reversal row, original flips to REVERSED. Works for every ticket
  // type (SPI reversal where supported, lot-offset otherwise).
  // F-BT-007: a grouped leg is reversed with its siblings, never alone.
  const handleReverseGroup = async (trade: BondTransaction) => {
    const ref = trade.legGroupRef;
    if (!ref) return;
    const reason = window.prompt(
      `Reverse leg group ${ref}?\n\nEvery posted leg of the group is reversed in one transaction ` +
      `(history stays replayable).\n\nReason (optional):`);
    if (reason === null) return;
    try {
      const res = await axios.post(`/api/trade-groups/${encodeURIComponent(ref)}/reverse`, { reason });
      alert(`✅ Leg group ${ref} reversed — status ${res.data?.status}`);
      fetchBondTransactions(true);
    } catch (error: any) {
      alert(`❌ Group reversal failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReverseTrade = async (trade: BondTransaction) => {
    if (!trade.id) { alert('Cannot reverse: Trade ID is missing'); return; }
    const reason = window.prompt(
      `Reverse trade #${trade.id}?\n\nThis removes its position and cash economics as-of today ` +
      `(history stays replayable) and cannot itself be undone except by rebooking.\n\nReason (optional):`);
    if (reason === null) return;
    try {
      const res = await axios.post(`/api/trade-transactions/${trade.id}/reverse`, { reason });
      alert(`✅ Trade #${trade.id} reversed (reversal trade #${res.data?.reversalTradeId})`);
      fetchBondTransactions(true);
    } catch (error: any) {
      alert(`❌ Reversal failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSendToVenue = async (venue: 'Tradeweb' | 'MarketAxess' | 'EMSX', trade: BondTransaction) => {
    if (!trade.id) {
      alert('Cannot send: Trade ID is missing');
      return;
    }

    const sessionName = venue.toUpperCase();

    setBlockingOperation({
      isActive: true,
      message: `Sending Order to ${venue}...`,
      subMessage: 'Connecting to FIX session and routing order'
    });

    try {
      let response;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          response = await axios.post('/api/fix/orders/send', {
            tradeTransactionId: trade.id,
            sessionName: sessionName
          });
          break;
        } catch (sendError: any) {
          const errorMsg = sendError.response?.data?.error || sendError.message || '';
          if ((errorMsg.includes('not registered') || errorMsg.includes('not logged on')) && retryCount === 0) {
            try {
              await axios.post('/api/fix/sessions/reload');
              await new Promise(resolve => setTimeout(resolve, 5000));
              retryCount++;
              continue;
            } catch (reloadError) {
              throw sendError;
            }
          } else {
            throw sendError;
          }
        }
      }
      
      if (response?.data.success) {
        alert(`✅ Order sent to ${venue} successfully!`);
        fetchBondTransactions();
      } else {
        alert(`❌ Failed to send to ${venue}: ${response?.data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      if (errorMsg.includes('not registered') || errorMsg.includes('not logged on')) {
        const reload = window.confirm(`❌ FIX session for ${venue} is not connected. Reload?`);
        if (reload) {
          try {
            await axios.post('/api/fix/sessions/reload');
            alert(`✅ FIX sessions reloaded. Please wait 5 seconds then try again.`);
          } catch (reloadError: any) {
            alert(`❌ Failed to reload sessions.`);
          }
        }
      } else {
        alert(`❌ Error sending to ${venue}:\n${errorMsg}`);
      }
    } finally {
      setBlockingOperation({ isActive: false, message: '', subMessage: '' });
    }
  };

  // Legacy LL seed history — fetched lazily when the toggle first turns on.
  useEffect(() => {
    if (!includeLegacyLL || legacyLLRows.length > 0) return;
    axios.get('/api/credit/secondary/transfers')
      .then(res => {
        const rows = (Array.isArray(res.data) ? res.data : [])
          .filter((t: any) => t.source === 'LEGACY')
          .map((t: any) => ({
            id: `L-${t.id}`,
            isLegacy: true,
            security: t.loanName || t.borrowerName || `Loan #${t.loanId}`,
            cusip: '',
            transactionType: t.direction,
            quantity: t.parAmount,
            price: t.transferPrice,
            amount: t.settlementAmount,
            tradeDate: t.tradeDate,
            settleDate: t.settlementDate,
            status: t.status || 'SETTLED',
            assetClass: 'Leveraged Loan',
            ticketType: 'LEVERAGED_LOAN',
            counterparty: t.counterparty,
            portfolio: '',
            assetManager: '',
            portfolioManager: '',
          }));
        setLegacyLLRows(rows);
      })
      .catch(() => setLegacyLLRows([]));
  }, [includeLegacyLL, legacyLLRows.length]);

  // Trades arrive filtered, ordered and paged by the server. Only the packages list and the
  // read-only legacy loan rows are filtered here.
  const filteredData = useMemo(() => {
    const matchesClientFilters = (t: any) => {
      if (filters.transactionType && t.transactionType !== filters.transactionType) return false;
      if (filters.status && t.status !== filters.status) return false;
      if (filters.portfolio && t.portfolio !== filters.portfolio) return false;
      if (filters.assetClass && t.assetClass !== filters.assetClass) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!t.cusip?.toLowerCase().includes(s) && !t.security?.toLowerCase().includes(s)
            && !String(t.id).toLowerCase().includes(s) && !t.portfolio?.toLowerCase().includes(s)) return false;
      }
      if (filters.tradeDateFrom && t.tradeDate < filters.tradeDateFrom) return false;
      if (filters.tradeDateTo && t.tradeDate > filters.tradeDateTo) return false;
      return true;
    };
    if (viewMode !== 'packages') {
      const legacy = includeLegacyLL && filters.assetClass === 'Leveraged Loan'
        ? legacyLLRows.filter(matchesClientFilters)
        : [];
      return [...allRowData, ...legacy];
    }
    return allRowData.filter(matchesClientFilters).sort((a: any, b: any) => {
      // Legacy 'L-#' ids are non-numeric — keep them below the live book.
      const na = Number(a.id); const nb = Number(b.id);
      if (Number.isNaN(na) && Number.isNaN(nb)) return String(b.id).localeCompare(String(a.id));
      if (Number.isNaN(na)) return 1;
      if (Number.isNaN(nb)) return -1;
      return nb - na;
    });
  }, [allRowData, filters, includeLegacyLL, legacyLLRows, viewMode]);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (viewMode === 'packages') {
      const open = (p: any) => { if (p.data?.legGroupRef) navigate(`/trade-entry/multi-leg?group=${encodeURIComponent(String(p.data.legGroupRef))}`); };
      return [
        { field: 'cusip', headerName: 'Package', flex: 2, cellStyle: { color: '#7dd3fc', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline' } as any, onCellClicked: open },
        { field: 'transactionType', headerName: 'Kind', width: 110 },
        { field: 'portfolio', headerName: 'Portfolio', flex: 2 },
        { field: 'amount', headerName: 'Legs', width: 90, type: 'numericColumn' },
        { field: 'status', headerName: 'Group Status', width: 220 },
        { field: 'tradeDate', headerName: 'Trade Date', width: 120 },
        { field: 'lastActivity', headerName: 'Last Activity', width: 170, valueFormatter: (p: any) => p.value ? String(p.value).replace('T', ' ').slice(0, 16) : '' },
      ];
    }
    const isAllocation = viewMode === 'allocation';
    const base: ColDef[] = [
      {
        field: 'id',
        headerName: 'ID',
        width: 90,
        pinned: 'left',
        sort: 'desc',
        comparator: (a: any, b: any) => Number(a) - Number(b),
        cellStyle: { fontWeight: 'bold', color: '#60a5fa' } as any,
        cellRenderer: (p: any) => {
          const ticketPath = getTradeEntryPathForRow(p.data || {});
          return (
            <button
              data-testid="blotter-open-trade"
              onClick={() => navigate(`/trade-entry/${ticketPath}`, { state: { id: p.value } })}
              className="text-blue-400 hover:text-blue-300 transition-colors font-bold underline decoration-blue-400/30 underline-offset-4"
            >
              {p.value}
            </button>
          );
        }
      },
      { 
        field: 'transactionType', 
        headerName: 'Type', 
        width: 112,
        cellRenderer: (p: any) => (
          <span className={cn(
            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border",
            isAbfNonCashEvent(p.data)
              ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/20"
              : p.value === 'BUY'
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/10"
                : "bg-red-500/10 text-red-400 border-red-500/10"
          )}>
            {formatTransactionType(p.data)}
          </span>
        )
      },
      { 
        field: 'status', 
        headerName: 'Status', 
        width: 120,
        cellRenderer: (p: any) => (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 border border-white/5">
            {p.value || 'DRAFT'}
          </span>
        )
      },
      { field: 'security', headerName: 'Security', flex: 2, cellStyle: { fontWeight: '600' } as any },
      {
        field: 'cusip',
        headerName: 'CUSIP',
        width: 120,
        cellStyle: { fontFamily: 'monospace' } as any,
        cellRenderer: (p: any) => {
          const cusip = p.value;
          if (!cusip) return null;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/securities?cusip=${encodeURIComponent(cusip)}`);
              }}
              className="text-blue-400 hover:text-blue-300 hover:underline transition-colors font-mono"
              title="Open in Security Master"
            >
              {cusip}
            </button>
          );
        }
      },
      { 
        field: isAllocation ? 'allocationAmount' : 'amount', 
        headerName: isAllocation ? 'Alloc Amount' : 'Amount', 
        width: 140, 
        cellRenderer: (p: any) => {
          if (isAbfNonCashEvent(p.data)) {
            return <span className="text-slate-400 font-sans text-xs font-semibold">Non-cash event</span>;
          }
          return p.value ? `$${Number(p.value).toLocaleString()}` : '';
        },
        cellStyle: { textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' } as any
      },
      { 
        field: isAllocation ? 'allocationPortfolio' : 'portfolio', 
        headerName: isAllocation ? 'Alloc Portfolio' : 'Portfolio', 
        width: 160 
      },
      { field: 'tradeDate', headerName: 'Trade Date', width: 120 },
      {
        field: 'legGroupRef',
        headerName: 'Leg Group',
        width: 150,
        valueFormatter: (p: any) => p.value ? String(p.value).replace(/^GRP-/, '') : '',
        tooltipValueGetter: (p: any) => p.value ? `Multi-leg group ${p.value} — advances only as a unit (F-BT-007)` : '',
        cellStyle: { color: '#7dd3fc', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' } as any,
        onCellClicked: (p: any) => { if (p.value) navigate(`/trade-entry/multi-leg?group=${encodeURIComponent(String(p.value))}`); },
      }
    ];
    return base;
  }, [viewMode, navigate]);

  const stats = useMemo(() => {
    if (viewMode !== 'packages' && summary) {
      return {
        total: summary.total,
        vol: (Number(summary.volume || 0) / 1000000).toFixed(1),
        buys: summary.buys,
        sells: summary.sells,
      };
    }
    const total = filteredData.length;
    const vol = filteredData.reduce((sum, t: any) => sum + (isAbfNonCashEvent(t) ? 0 : (t.amount || 0)), 0);
    const buys = filteredData.filter((t: any) => t.transactionType === 'BUY').length;
    const sells = filteredData.filter((t: any) => t.transactionType === 'SELL').length;
    return { total, vol: (vol / 1000000).toFixed(1), buys, sells };
  }, [filteredData, summary, viewMode]);

  // Server-sortable columns keep the server's order; the rest are not sortable on a page of rows.
  const gridColumnDefs = useMemo<ColDef[]>(() => {
    if (viewMode === 'packages') return columnDefs;
    return columnDefs.map(col => {
      const field = typeof col.field === 'string' ? col.field : undefined;
      if (field && SERVER_SORT_FIELDS[field]) {
        return { ...col, sortable: true, comparator: () => 0, sort: field === serverSort.field ? serverSort.dir : null };
      }
      return { ...col, sortable: false };
    });
  }, [columnDefs, viewMode, serverSort]);

  const legacyShown = filteredData.length - allRowData.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const firstShown = totalRows === 0 ? 0 : page * pageSize + 1;
  const lastShown = Math.min(totalRows, (page + 1) * pageSize);

  return (
    <Container>
      <BlockingOverlay isVisible={blockingOperation.isActive} message={blockingOperation.message} subMessage={blockingOperation.subMessage} showHourglass />
      
      <div className="max-w-[1800px] mx-auto space-y-8">
        {/* Header & Stats */}
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                Trade Blotter
              </h1>
              <p className="text-slate-500 font-medium mt-1">Real-time execution monitoring and multi-asset transaction management</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => fetchBondTransactions(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white hover:bg-slate-800 transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
              <button 
                onClick={() => navigate('/trade-entry/fixed-income')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-xl text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all"
              >
                <PlusCircle className="w-4 h-4" />
                New Trade
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Total Trades" value={String(stats.total)} icon={<Activity className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Volume" value={`$${stats.vol}M`} icon={<TrendingUp className="w-5 h-5" />} color="#8b5cf6" />
            <StatCard label="Buy Orders" value={String(stats.buys)} icon={<Zap className="w-5 h-5" />} color="#10b981" />
            <StatCard label="Sell Orders" value={String(stats.sells)} icon={<Package className="w-5 h-5" />} color="#f59e0b" />
          </div>

          {/* Filter Bar */}
          <GlassCard className="p-4 bg-slate-900/40 border-white/5">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Filters:</span>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block ml-1">Status</label>
                  <select 
                    value={filters.status || ''} 
                    onChange={(e) => setFilters({...filters, status: e.target.value || undefined})}
                    className="bg-slate-950 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 transition-all min-w-[120px]"
                  >
                    <option value="">All Statuses</option>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block ml-1">Type</label>
                  <select 
                    value={filters.transactionType || ''} 
                    onChange={(e) => setFilters({...filters, transactionType: e.target.value || undefined})}
                    className="bg-slate-950 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 transition-all min-w-[120px]"
                  >
                    <option value="">All Types</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block ml-1">Portfolio</label>
                  <select
                    value={filters.portfolio || ''}
                    onChange={(e) => setFilters({...filters, portfolio: e.target.value || undefined})}
                    className="bg-slate-950 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 transition-all min-w-[150px]"
                  >
                    <option value="">All Portfolios</option>
                    {portfolios.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block ml-1">Asset Class</label>
                  <select
                    value={filters.assetClass || ''}
                    onChange={(e) => setFilters({...filters, assetClass: e.target.value || undefined})}
                    className="bg-slate-950 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 transition-all min-w-[150px]"
                  >
                    <option value="">All Asset Classes</option>
                    <option value="Fixed Income">Fixed Income</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Private Equity">Private Equity</option>
                    <option value="Private Credit">Private Credit</option>
                    <option value="Asset-Based Finance">Asset-Based Finance</option>
                    <option value="Leveraged Loan">Leveraged Loan</option>
                    <option value="CLO">CLO</option>
                    <option value="Derivatives">Derivatives</option>
                    <option value="FX">FX</option>
                  </select>
                  {filters.assetClass === 'Leveraged Loan' && (
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-400 ml-1 mt-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeLegacyLL}
                        onChange={(e) => setIncludeLegacyLL(e.target.checked)}
                        className="accent-indigo-500"
                      />
                      Include legacy seed history (L-# rows, read-only)
                    </label>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block ml-1">Date From</label>
                  <DateField compact
                    value={filters.tradeDateFrom || ''}
                    onChange={iso => setFilters({...filters, tradeDateFrom: iso || undefined})}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block ml-1">Date To</label>
                  <DateField compact
                    value={filters.tradeDateTo || ''}
                    onChange={iso => setFilters({...filters, tradeDateTo: iso || undefined})}
                  />
                </div>
              </div>

              <div className="ml-auto flex items-center gap-3">
                <button 
                  onClick={() => setFilters({})}
                  className="text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-widest transition-colors px-3 py-1.5 rounded-lg border border-white/5 hover:bg-white/5"
                >
                  Clear All
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Main Grid Card */}
        <GlassCard className="flex flex-col h-[calc(100vh-400px)] min-h-[500px]">
          <div className="p-4 border-b border-white/5 flex items-center justify-between bg-slate-900/30">
            <div className="flex items-center gap-4">
              <div className="flex bg-slate-950/50 p-1 rounded-xl border border-white/5">
                <button 
                  onClick={() => setViewMode('block')}
                  className={cn("px-4 py-1.5 rounded-lg text-xs font-bold transition-all", viewMode === 'block' ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300")}
                >
                  Block View
                </button>
                <button 
                  onClick={() => setViewMode('allocation')}
                  className={cn("px-4 py-1.5 rounded-lg text-xs font-bold transition-all", viewMode === 'allocation' ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300")}
                >
                  Allocation View
                </button>
                <button 
                  onClick={() => setViewMode('packages')}
                  className={cn("px-4 py-1.5 rounded-lg text-xs font-bold transition-all", viewMode === 'packages' ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300")}
                  title="Multi-leg packages (rolls / swaps) — legs advance only as a unit"
                >
                  Packages
                </button>
              </div>
              {viewMode !== 'packages' && (
                <div className="flex bg-slate-950/50 p-1 rounded-xl border border-white/5" role="group" aria-label="Which trades to list">
                  {SCOPES.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setScope(option)}
                      disabled={searchActive}
                      title={searchActive ? 'A search covers every trade' : SCOPE_LABELS[option].hint}
                      aria-pressed={scope === option && !searchActive}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
                        scope === option && !searchActive ? "bg-slate-700 text-white shadow" : "text-slate-500 hover:text-slate-300",
                        searchActive && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {SCOPE_LABELS[option].label}
                    </button>
                  ))}
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input 
                  type="text" 
                  placeholder="Search ID, CUSIP, security, portfolio, counterparty"
                  value={filters.search || ''}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-9 pr-4 py-2 bg-slate-950/50 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 transition-all w-80"
                />
              </div>
              {searchActive && viewMode !== 'packages' && (
                <span className="text-[11px] text-slate-500">Searching every trade</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 text-slate-500 hover:text-white transition-colors"><Download className="w-4 h-4" /></button>
              <button className="p-2 text-slate-500 hover:text-white transition-colors"><MessageSquare className="w-4 h-4" /></button>
            </div>
          </div>

          {loadError && (
            <div className="px-4 py-2 text-xs text-red-300 bg-red-500/10 border-b border-red-500/20" role="alert">{loadError}</div>
          )}
          <div className="flex-1 relative ag-theme-alpine-dark" onContextMenu={(e) => e.preventDefault()}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={gridColumnDefs}
              animateRows={true}
              pagination={viewMode === 'packages'}
              paginationPageSize={20}
              headerHeight={48}
              rowHeight={44}
              defaultColDef={{ resizable: true, sortable: viewMode === 'packages', filter: viewMode === 'packages' }}
              onSortChanged={(event) => {
                if (viewMode === 'packages') return;
                const sorted = event.columnApi.getColumnState().find(column => column.sort);
                const field = sorted?.colId;
                const dir = sorted?.sort === 'asc' ? 'asc' : 'desc';
                const next = field && SERVER_SORT_FIELDS[field] ? { field, dir } as const : { field: 'id', dir: 'desc' } as const;
                setServerSort(prev => (prev.field === next.field && prev.dir === next.dir ? prev : { ...next }));
              }}
              onGridReady={(p) => p.api.sizeColumnsToFit()}
              onCellContextMenu={(params) => {
                const event = params.event as any;
                event?.preventDefault();
                setContextMenu({
                  x: event?.clientX || 0,
                  y: event?.clientY || 0,
                  trade: params.data
                });
              }}
              getRowStyle={(params: any) => ({
                backgroundColor: params.node.rowIndex % 2 === 0 ? 'rgba(15, 23, 42, 0.3)' : 'transparent',
                borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
              })}
            />
          </div>
          {viewMode !== 'packages' && (
            <div className="px-4 py-3 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
              <span>
                {totalRows === 0
                  ? 'No trades match'
                  : `Showing ${firstShown.toLocaleString()}–${lastShown.toLocaleString()} of ${totalRows.toLocaleString()} trades`}
                {legacyShown > 0 ? ` · plus ${legacyShown} legacy rows` : ''}
              </span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5">
                  <span>Rows</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="bg-slate-950 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none"
                  >
                    {PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => setPage(current => Math.max(0, current - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span>Page {(page + 1).toLocaleString()} of {pageCount.toLocaleString()}</span>
                <button
                  type="button"
                  onClick={() => setPage(current => Math.min(pageCount - 1, current + 1))}
                  disabled={page + 1 >= pageCount}
                  className="px-3 py-1 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </GlassCard>
      </div>

      <AIChatPanel />
      {contextMenu && contextMenu.trade && (() => {
        const trade = contextMenu.trade;
        const status = (trade.status || '').toUpperCase();
        const isSent = status === 'SENT';
        const isExecuted = status === 'EXECUTED' || status === 'POSTED';
        const isGovBond = isGovernmentBond(trade.assetClass || trade.assetClassName || '');
        
        let menuItems: any[] = [];
        
        if (isSent) {
          menuItems = [
            {
              label: isGovBond ? 'Send to Tradeweb' : 'Send to MarketAxess',
              icon: <Activity className="w-4 h-4" />,
              onClick: () => handleSendToVenue(isGovBond ? 'Tradeweb' : 'MarketAxess', trade)
            },
            {
              label: isGovBond ? 'Send to MarketAxess (Alternative)' : 'Send to Tradeweb (Alternative)',
              icon: <Activity className="w-4 h-4" />,
              onClick: () => handleSendToVenue(isGovBond ? 'MarketAxess' : 'Tradeweb', trade)
            },
            {
              label: 'Send to Bloomberg (EMSX)',
              icon: <Zap className="w-4 h-4" />,
              onClick: () => handleSendToVenue('EMSX', trade)
            }
          ];
        } else if (isExecuted) {
          menuItems = [
            {
              label: isGovBond ? 'Send Allocation to Tradeweb' : 'Send Allocation to MarketAxess',
              icon: <Layers className="w-4 h-4" />,
              onClick: () => handleSendAllocation(isGovBond ? 'Tradeweb' : 'MarketAxess', trade)
            },
            {
              label: isGovBond ? 'Send Allocation to MarketAxess (Alternative)' : 'Send Allocation to Tradeweb (Alternative)',
              icon: <Layers className="w-4 h-4" />,
              onClick: () => handleSendAllocation(isGovBond ? 'MarketAxess' : 'Tradeweb', trade)
            },
            {
              label: 'Send Allocation to Bloomberg (EMSX)',
              icon: <Zap className="w-4 h-4" />,
              onClick: () => handleSendAllocation('EMSX', trade)
            }
          ];
          if (status === 'POSTED') {
            menuItems.push(trade.legGroupRef ? {
              label: `Reverse Leg Group ${String(trade.legGroupRef).replace(/^GRP-/, '')}…`,
              icon: <RefreshCcw className="w-4 h-4" />,
              onClick: () => handleReverseGroup(trade)
            } : {
              label: 'Reverse Trade…',
              icon: <RefreshCcw className="w-4 h-4" />,
              onClick: () => handleReverseTrade(trade)
            });
          }
        } else {
          menuItems = [
            {
              label: `Status: ${status || 'DRAFT'} - Pre-trade routing available for SENT trades`,
              disabled: true
            }
          ];
        }

        if (trade.isPackage) {
          menuItems = [
            {
              label: `Open package ${String(trade.legGroupRef).replace(/^GRP-/, '')}`,
              icon: <Layers className="w-4 h-4" />,
              onClick: () => navigate(`/trade-entry/multi-leg?group=${encodeURIComponent(trade.legGroupRef!)}`)
            },
          ];
          if (status === 'POSTED') {
            menuItems.push({
              label: 'Reverse package…',
              icon: <RefreshCcw className="w-4 h-4" />,
              onClick: () => handleReverseGroup(trade)
            });
          }
        } else if (trade.legGroupRef) {
          menuItems = [
            {
              label: `Open Leg Group ${String(trade.legGroupRef).replace(/^GRP-/, '')} (advance all legs together)`,
              icon: <Layers className="w-4 h-4" />,
              onClick: () => navigate(`/trade-entry/multi-leg?group=${encodeURIComponent(trade.legGroupRef!)}`)
            },
            ...menuItems,
          ];
        }

        return (
          <ContextMenu 
            x={contextMenu.x} 
            y={contextMenu.y} 
            items={menuItems} 
            onClose={() => setContextMenu(null)} 
          />
        );
      })()}
    </Container>
  );
};

export default BondTransactionScreen;
